/**
 * @fileoverview MergeHistoryDrawer — a slide-in drawer that shows the full
 * merge history for a given master venue.
 *
 * Triggered by clicking a `MergedToId` chip in the Master/Detail table.
 * Fetches merge history from `GET /MasterVenues/GetMergeHistory` and displays
 * each merge step as an old → new location card with city/state, address,
 * updated time, and updated-by. Each step also lists the merged child
 * mapping rows (Market, Venue ID, Venue Name, City) so users can see exactly
 * which rows were merged.
 */

import * as React from 'react';
import { useDataProvider } from 'react-admin';
import {
    Box,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';
import { getMarketType } from '../utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single market-venue mapping row that was re-linked during a merge step,
 * shown in the Merge History view so users can see which child rows were merged.
 */
interface MergedMarketRow {
    /** Numeric market identifier. */
    marketId: number;
    /** Market type string (e.g. "sm", "axs"). */
    marketType: string;
    /** Source venue identifier within the market's system. */
    sourceVenueId: number;
    /** Venue display name from the source market's data, at merge time. */
    venueName: string | null;
    /** City from the source market's venue record, at merge time. */
    city: string | null;
}

/**
 * A single merge-history entry returned by `GET /MasterVenues/GetMergeHistory`.
 */
interface MergeHistoryEntry {
    /** ID of the deprecated source master venue. */
    sourceMasterVenueId: number;
    /** Google unified name of the source venue. */
    sourceGoogleUnifiedName: string | null;
    /** Formatted address of the source venue. */
    sourceFormattedAddress: string | null;
    /** City of the source venue. */
    sourceCity: string | null;
    /** State abbreviation of the source venue. */
    sourceState: string | null;
    /** ID of the canonical destination master venue. */
    destinationMasterVenueId: number;
    /** Google unified name of the destination venue. */
    destinationGoogleUnifiedName: string | null;
    /** Formatted address of the destination venue. */
    destinationFormattedAddress: string | null;
    /** City of the destination venue. */
    destinationCity: string | null;
    /** State abbreviation of the destination venue. */
    destinationState: string | null;
    /** UTC timestamp when the merge was performed. */
    updatedOn: string | null;
    /** User who performed the merge. */
    updatedBy: string | null;
    /**
     * Market-venue mappings re-linked from source to destination during this
     * merge step — the specific child rows that were merged.
     */
    mergedMarkets: MergedMarketRow[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link MergeHistoryDrawer}. */
export interface MergeHistoryDrawerProps {
    /** Whether the drawer is open. */
    open: boolean;
    /** The master venue ID whose merge history is being shown. */
    masterVenueId: number | null;
    /** Called when the user closes the drawer. */
    onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a UTC ISO date string for display, returning `—` when falsy.
 *
 * @param value - ISO date string or null/undefined.
 * @returns Locale date+time string.
 */
function formatDate (value: string | null | undefined): string {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

/**
 * Builds a "City, State" string from individual parts, returning `—` when both absent.
 *
 * @param city  - City name.
 * @param state - State abbreviation.
 */
function cityState (city: string | null | undefined, state: string | null | undefined): string {
    return [city, state].filter(Boolean).join(', ') || '—';
}

// ---------------------------------------------------------------------------
// LocationBlock — compact venue info for one side of a merge step
// ---------------------------------------------------------------------------

/** Props for {@link LocationBlock}. */
interface LocationBlockProps {
    /** Section label, e.g. "Before (merged away)". */
    label: string;
    /** Display color for the label chip — drives the label text color. */
    labelColor: 'error' | 'success';
    /** Venue name to display. */
    unifiedName: string | null | undefined;
    /** City and state abbreviation (pre-formatted). */
    cityStateStr: string;
    /** Full formatted address. Used for display and as the map embed query. */
    formattedAddress: string | null | undefined;
}

/**
 * A compact block showing venue name, city/state, address, and an embedded
 * Google Maps iframe for one side of a merge-step card.
 *
 * Label and text colors use opacity-based theme tokens (`error.main`,
 * `success.main`, `text.secondary`, `text.disabled`) so the block renders
 * correctly in both light and dark modes without any hard-coded black values.
 * The map iframe container uses `borderColor: 'divider'` (also theme-aware)
 * and a translucent skeleton placeholder.
 */
const LocationBlock: React.FC<LocationBlockProps> = ({
    label,
    labelColor,
    unifiedName,
    cityStateStr,
    formattedAddress,
}) => {
    const [iframeLoaded, setIframeLoaded] = React.useState(false);

    // Reset iframe load state whenever the address changes.
    React.useEffect(() => { setIframeLoaded(false); }, [formattedAddress]);

    const embedSrc = formattedAddress
        ? `https://maps.google.com/maps?q=${encodeURIComponent(formattedAddress)}&output=embed`
        : null;

    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Label — uses theme-aware color with opacity to work in light & dark */}
            <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: `${labelColor}.main`, opacity: 0.9 }}
                display="block"
                gutterBottom
            >
                {label}
            </Typography>

            <Typography variant="body2" fontWeight={600} noWrap>
                {unifiedName || '(unnamed)'}
            </Typography>

            <Typography variant="caption" color="text.secondary" display="block">
                {cityStateStr}
            </Typography>

            {formattedAddress && (
                <Typography
                    variant="caption"
                    color="text.disabled"
                    display="block"
                    noWrap
                    title={formattedAddress}
                >
                    {formattedAddress}
                </Typography>
            )}

            {/* Embedded Google Maps iframe — mirrors UnifiedNameDrawer approach */}
            {embedSrc && (
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: 140,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: 1,
                        borderColor: 'divider',
                        mt: 0.75,
                    }}
                >
                    {!iframeLoaded && (
                        <Skeleton
                            variant="rectangular"
                            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                        />
                    )}
                    <Box
                        component="iframe"
                        src={embedSrc}
                        title={`Map preview for ${unifiedName || formattedAddress}`}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        onLoad={() => setIframeLoaded(true)}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                            opacity: iframeLoaded ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// MergedMarketsTable — merged child rows for one merge step
// ---------------------------------------------------------------------------

/** Props for {@link MergedMarketsTable}. */
interface MergedMarketsTableProps {
    /** Merged child mapping rows to display. */
    rows: MergedMarketRow[];
}

/**
 * Lists the individual `Market_Venue_Mappings` child rows that were re-linked
 * during a merge step, so users can see exactly which rows were merged.
 * Shows Market, Venue ID, Venue Name, and City for each row.
 *
 * @param props.rows - Merged child mapping rows for one merge-history entry.
 */
const MergedMarketsTable: React.FC<MergedMarketsTableProps> = ({ rows }) => {
    if (!rows.length) return null;

    return (
        <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Market</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Venue ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Venue Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>City</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, i) => (
                    <TableRow key={`${row.marketId}-${row.marketType}-${row.sourceVenueId}-${i}`}>
                        <TableCell sx={{ py: 0.5 }}>{getMarketType(row.marketId, row.marketType)}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{row.sourceVenueId}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{row.venueName || '—'}</TableCell>
                        <TableCell sx={{ py: 0.5, color: 'text.secondary' }}>{row.city || '—'}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * A right-side `Drawer` that fetches and displays the merge history for a
 * master venue identified by {@link MergeHistoryDrawerProps.masterVenueId}.
 *
 * Data is loaded from `GET /MasterVenues/GetMergeHistory?masterVenueId={id}`.
 * Each entry renders as an old → new location card, showing city/state,
 * formatted address, updated time, and the user who performed the merge.
 *
 * @param props.open           - Controls drawer visibility.
 * @param props.masterVenueId  - Master venue whose merge history to show.
 * @param props.onClose        - Close callback.
 *
 * @example
 * <MergeHistoryDrawer
 *   open={drawerOpen}
 *   masterVenueId={selectedMergedToId}
 *   onClose={() => setDrawerOpen(false)}
 * />
 */
const MergeHistoryDrawer: React.FC<MergeHistoryDrawerProps> = ({
    open,
    masterVenueId,
    onClose,
}) => {
    const dataProvider = useDataProvider() as any;
    const [entries, setEntries] = React.useState<MergeHistoryEntry[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    // Fetch merge history whenever the drawer opens for a (new) master venue.
    React.useEffect(() => {
        if (!open || !masterVenueId) {
            setEntries([]);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        dataProvider
            .fetchData('MasterVenues', `GetMergeHistory/${masterVenueId}`)
            .then((res: { data: MergeHistoryEntry[] }) => {
                const data = (res.data ?? []).map(entry => ({
                    ...entry,
                    mergedMarkets: entry.mergedMarkets ?? [],
                }));
                setEntries(data);
            })
            .catch((err: any) => {
                setError(err?.message ?? 'Failed to load merge history.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [open, masterVenueId, dataProvider]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 750 } } }}
        >
            {/* ── Header ──────────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
                <HistoryIcon color="primary" />
                <Typography variant="h6" sx={{ flex: 1, fontSize: '1rem', fontWeight: 600 }} noWrap>
                    Merge History — Venue #{masterVenueId}
                </Typography>
                <IconButton size="small" onClick={onClose} aria-label="close drawer">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Divider />

            {/* ── Body ────────────────────────────────────────────────────────── */}
            <Box sx={{ px: 2, py: 1.5, overflow: 'auto' }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && error && (
                    <Typography variant="body2" color="error">
                        {error}
                    </Typography>
                )}

                {!loading && !error && entries.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No merge history found for this venue.
                    </Typography>
                )}

                {!loading && !error && entries.map((entry, i) => (
                    <Paper
                        key={`${entry.sourceMasterVenueId}-${i}`}
                        variant="outlined"
                        sx={{ mb: 2, p: 1.5 }}
                    >
                        {/* Old → New location row */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <LocationBlock
                                label="Before (merged away)"
                                labelColor="error"
                                unifiedName={entry.sourceGoogleUnifiedName}
                                cityStateStr={cityState(entry.sourceCity, entry.sourceState)}
                                formattedAddress={entry.sourceFormattedAddress}
                            />

                            <Box sx={{ pt: 2.5, color: 'text.disabled', flexShrink: 0 }}>
                                <ArrowForwardIcon fontSize="small" />
                            </Box>

                            <LocationBlock
                                label="After (merged into)"
                                labelColor="success"
                                unifiedName={entry.destinationGoogleUnifiedName}
                                cityStateStr={cityState(entry.destinationCity, entry.destinationState)}
                                formattedAddress={entry.destinationFormattedAddress}
                            />
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        {/* Metadata row */}
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Updated
                                </Typography>
                                <Typography variant="caption" fontWeight={500}>
                                    {formatDate(entry.updatedOn)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    By
                                </Typography>
                                <Typography variant="caption" fontWeight={500}>
                                    {entry.updatedBy || '—'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Merged child rows — which mappings were part of this merge */}
                        {entry.mergedMarkets.length > 0 && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Merged rows
                                </Typography>
                                <MergedMarketsTable rows={entry.mergedMarkets} />
                            </>
                        )}
                    </Paper>
                ))}
            </Box>
        </Drawer>
    );
};

export default MergeHistoryDrawer;
